import React, { useEffect, useState, useCallback } from 'react';
import { SIGNUP_ENABLED } from '../../config/prelaunch';

/**
 * FEAST ORDER ランディングページ（ルート "/"）
 * - Web / iOS / Android アプリの紹介LP
 * - 日英バイリンガル（言語切替）
 * - SEO: <head> の title / description / canonical / hreflang / OGP / 構造化データ(JSON-LD) を制御
 * - アプリ画面は public/lp/shot-*.png（実画面のスクリーンショット）を使用
 *
 * ストア公開後は下記の APP_STORE_URL / GOOGLE_PLAY_URL に URL を設定すれば
 * 「準備中」表示が自動でリンクに切り替わる。
 */

type Lang = 'ja' | 'en';

// ===== 外部リンク・公開状態の集中管理 =====
const CORP_URL = 'https://corp.feast.co.jp';
const CORP_SERVICE_URL = `${CORP_URL}/service/`;
const CORP_CONTACT_URL = `${CORP_URL}/about/`;
const SITE_URL = 'https://order.feast.co.jp/';
const OGP_IMAGE = `${SITE_URL}lp/ogp.png`;

// 公開後にストアURLを設定（空文字なら「準備中 / Coming soon」表示）
const APP_STORE_URL = '';
const GOOGLE_PLAY_URL = '';

const ASSET = (p: string): string => `${process.env.PUBLIC_URL || ''}${p}`;

// ===== コンテンツ（日英） =====
interface Feature {
  title: string;
  body: string;
}
interface Screen {
  src: string;
  alt: string;
  caption: string;
  desc: string;
  frame: 'phone' | 'browser';
}
interface Copy {
  htmlLang: string;
  metaTitle: string;
  metaDescription: string;
  nav: { service: string; corp: string };
  hero: {
    badge: string;
    title: string;
    lead: string;
    primaryCta: string;
    secondaryCta: string;
    note: string;
  };
  platforms: { heading: string; web: string; webState: string; comingSoon: string };
  screensHeading: string;
  screensSub: string;
  screens: Screen[];
  featuresHeading: string;
  features: Feature[];
  proof: {
    heading: string;
    sub: string;
    stats: { value: string; label: string }[];
    body: string;
  };
  subscription: {
    heading: string;
    body: string;
    points: string[];
    cta: string;
  };
  ctaBand: { title: string; body: string; button: string };
  footer: { corp: string; service: string; rights: string };
  langLabel: string;
}

const COPY: Record<Lang, Copy> = {
  ja: {
    htmlLang: 'ja',
    metaTitle: 'FEAST ORDER｜飲食店向け店内オーダー・予約管理システム（Web・iOS・Android）',
    metaDescription:
      'FEAST ORDERは、お客様のスマホから注文・Web予約でき、スタッフはリアルタイムに受付できる飲食店向けの店内オーダー・予約管理システムです。Web・iOS・Androidに対応し、サブスクリプションでご提供します。',
    nav: { service: 'サービス詳細', corp: '運営会社' },
    hero: {
      badge: '店内オーダー ＋ 予約管理 / Web ・ iOS ・ Android 対応',
      title: '店内オーダーも予約も、もっとシンプルに。',
      lead: 'お客様は席のQRコードからスマホで注文、来店前はWebから予約。スタッフはリアルタイムに受付。FEAST ORDERは、狭小・小規模の飲食店のために設計された店内オーダー・予約管理システムです。',
      primaryCta: '無料で始める（30日間無料）',
      secondaryCta: 'サービス詳細を見る',
      note: '資料請求・営業とのやり取りは不要。Web からその場で無料トライアルを開始できます。',
    },
    platforms: {
      heading: 'マルチデバイスで使える',
      web: 'Web版',
      webState: '8月公開に向けて準備中',
      comingSoon: '準備中',
    },
    screensHeading: 'アプリ画面',
    screensSub: 'お客様・スタッフ・管理者、それぞれの画面を1つのサービスで。',
    screens: [
      {
        src: ASSET('/lp/app-store-table.png'),
        alt: 'FEAST ORDER 店舗管理画面',
        caption: '店舗管理',
        desc: '開店・閉店からテーブルの利用開始・来店人数の記録、店員オーダー・会計まで。店舗の一日をこの画面で回せます。',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-menu-mng.png'),
        alt: 'FEAST ORDER メニュー管理画面',
        caption: 'メニュー管理',
        desc: 'メニューとカテゴリを一覧で管理。並べ替えも価格やオプションの編集も、その場で完結します。',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-bill.png'),
        alt: 'FEAST ORDER 会計画面',
        caption: 'テーブル会計',
        desc: '注文はそのまま会計明細に。合計・税額・オプションまでテーブル単位で確認し、その場で会計を確定できます。',
        frame: 'phone',
      },
      // 予約系スクリーンショットはスタッフ向け iOSアプリの実機画面（phoneフレーム）（ADR 0046）。
      // 公開予約フォームは顧客向けWebのため iOSアプリには無く、スクショ対象外（文章でのみ訴求）。
      {
        src: ASSET('/lp/app-reservation-board.png'),
        alt: 'FEAST ORDER 予約台帳画面（席タイプ別タイムライン）',
        caption: '予約台帳',
        desc: '席タイプ×卓数の時間軸レーンで、その日の予約と空き・オーバーブックをひと目で把握できます。',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-reservation-list.png'),
        alt: 'FEAST ORDER 予約一覧・時間帯サマリ画面',
        caption: '予約一覧',
        desc: '時間帯ごとの来店人数を可視化。ステータスや日付で予約を絞り込んで確認できます。',
        frame: 'phone',
      },
    ],
    featuresHeading: '主な機能',
    features: [
      { title: 'QRオーダー', body: '席のQRコードから、お客様のスマホで完結。アプリのインストールは不要です。' },
      { title: 'リアルタイム通知', body: '新規注文や店員呼び出しを、Web（SSE）・モバイル（プッシュ通知）で即座にお知らせ。' },
      { title: 'かんたんメニュー管理', body: 'カテゴリ・オプション・写真をドラッグ&ドロップで直感的に編集できます。' },
      { title: '予約管理・台帳', body: '席タイプ×卓数で空き枠を管理。リストと台帳（席タイプ別の時間軸レーン）の2ビューで予約をひと目で把握。' },
      { title: 'Web予約フォーム', body: '店舗ごとの公開ページから4ステップで予約完了。スマホ最適化・日英対応・コースの税込表示にも対応。' },
      { title: '予約の照会・キャンセル', body: 'お客様は予約番号でご自身の予約を照会。期限内ならセルフキャンセルもでき、電話対応の手間を減らします。' },
      { title: 'マルチデバイス', body: 'Webブラウザ、iOS / Androidアプリ。お店のスタイルに合わせて選べます。' },
      { title: 'メニューオプション', body: '必須・任意のオプションや価格変動に対応。複雑な注文も正確に。' },
      { title: 'マルチ店舗対応', body: '複数店舗の運営も、1つのアカウントで切り替えて管理できます。' },
      { title: '店員オーダー・会計', body: 'スタッフによる代理注文から、テーブルごとの会計確定まで1つの画面で完結します。' },
      { title: 'テーブル・来店管理', body: 'テーブルの利用開始と来店人数を記録。稼働中のテーブルをひと目で把握できます。' },
      { title: 'Stripe 決済', body: 'サブスクリプションの契約・カード変更は Stripe のセキュアな決済ページで完結。店舗の Stripe アカウント連携にも対応。' },
    ],
    proof: {
      heading: '現場で毎日、使われています',
      sub: '代表自身が経営する飲食店「小料理紫雲」（東京・大井町）で、店内オーダーを2025年1月から毎日実運用。現場で使い込みながら開発を続けています。予約管理は1stリリース後に追加した新機能で、こちらもこれから現場で磨き込んでいきます。',
      stats: [
        { value: '1年半', label: '実店舗での運用実績' },
        { value: 'ワンオペ', label: '2フロアの狭小店を一人で運営' },
      ],
      body: 'グルメサイト連携のような複雑な機能はあえて持たず、小規模店に本当に必要な注文まわりだけをシンプルに。ワンオペの狭小店でも迷わず回せるまで磨き込んだ操作性を、そのままお届けします。',
    },
    subscription: {
      heading: 'サブスクリプションで、いつも最新',
      body: '高額な初期費用は不要。月額のサブスクリプションで導入でき、機能アップデートも継続的に提供します。',
      points: [
        '店内オーダー・予約管理の両方が基本プランに含まれる',
        '初期費用を抑えてスモールスタート',
        'Webからクレジットカードでそのまま契約（Stripe決済）',
        '機能アップデートを継続提供',
      ],
      cta: '無料で始める（新規登録）',
    },
    ctaBand: {
      title: '今日から無料で試せます',
      body: '資料請求・訪問営業はありません。Google または Apple アカウントがあれば数分で開始でき、30日間の無料トライアル後に月額 ¥3,300（税込）です。',
      button: '無料で始める（新規登録）',
    },
    footer: { corp: '運営会社（コーポレートサイト）', service: 'サービス詳細', rights: 'FEAST株式会社' },
    langLabel: 'English',
  },
  en: {
    htmlLang: 'en',
    metaTitle: 'FEAST ORDER｜In-store ordering & reservation system for restaurants (Web · iOS · Android)',
    metaDescription:
      'FEAST ORDER lets guests order from their own phones and book online, while staff receive orders in real time and manage reservations on a timeline board. Built for small restaurants, available on Web, iOS and Android as a subscription service.',
    nav: { service: 'Service', corp: 'Company' },
    hero: {
      badge: 'In-store ordering + Reservations / Web · iOS · Android',
      title: 'In-store ordering and reservations, made simple.',
      lead: 'Guests order from their phones via a table QR code and book online before visiting, while staff receive everything in real time. FEAST ORDER is an in-store ordering and reservation system designed for small dining establishments.',
      primaryCta: 'Start free (30-day trial)',
      secondaryCta: 'See the service',
      note: 'No sales calls or paperwork — start your free trial right away on the web.',
    },
    platforms: {
      heading: 'Works across devices',
      web: 'Web',
      webState: 'Coming August 2026',
      comingSoon: 'Coming soon',
    },
    screensHeading: 'App screens',
    screensSub: 'Guest, staff and admin experiences — all in one service.',
    screens: [
      {
        src: ASSET('/lp/app-store-table.png'),
        alt: 'FEAST ORDER store management screen',
        caption: 'Store management',
        desc: 'From opening up to starting tables, recording party sizes, clerk orders and billing — run the whole day from one screen.',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-menu-mng.png'),
        alt: 'FEAST ORDER menu management screen',
        caption: 'Menu management',
        desc: 'Manage menus and categories in one list — reorder and edit prices and options on the spot.',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-bill.png'),
        alt: 'FEAST ORDER table billing screen',
        caption: 'Table billing',
        desc: 'Orders roll straight into the bill. Review totals and tax per table and settle on the spot.',
        frame: 'phone',
      },
      // Reservation screenshots (staff iOS app, phone frame) — ADR 0046.
      // The guest-facing online booking form lives on the web, not the iOS app, so it is not captured (promoted in copy only).
      {
        src: ASSET('/lp/app-reservation-board.png'),
        alt: 'FEAST ORDER reservation timeline board',
        caption: 'Reservation board',
        desc: 'See the whole day at a glance on a per-seat-type, per-slot timeline — bookings, availability and overbooking.',
        frame: 'phone',
      },
      {
        src: ASSET('/lp/app-reservation-list.png'),
        alt: 'FEAST ORDER reservation list with hourly summary',
        caption: 'Reservation list',
        desc: 'Visualize guests by time slot, and filter bookings by status and date.',
        frame: 'phone',
      },
    ],
    featuresHeading: 'Key features',
    features: [
      { title: 'QR ordering', body: 'Guests order from their own phone via the table QR code. No app install needed.' },
      { title: 'Real-time alerts', body: 'New orders and staff calls are pushed instantly to Web (SSE) and mobile (push notifications).' },
      { title: 'Easy menu management', body: 'Edit categories, options and photos intuitively with drag & drop.' },
      { title: 'Reservation board', body: 'Manage availability by seat type and party slots, with list and timeline-board views of every booking.' },
      { title: 'Online booking form', body: 'Guests book in 4 steps on your public page — mobile-optimized, bilingual (ja/en), with tax-included course pricing.' },
      { title: 'Self-service lookup & cancel', body: 'Guests can look up their booking by number and cancel within the deadline — fewer phone calls for your staff.' },
      { title: 'Multi-device', body: 'Web browser, iOS and Android apps — choose what fits your restaurant.' },
      { title: 'Menu options', body: 'Required and optional choices with price changes, so complex orders stay accurate.' },
      { title: 'Multiple stores', body: 'Run several locations and switch between them from a single account.' },
      { title: 'Staff ordering & billing', body: 'From orders taken by staff to settling each table, everything happens in one screen.' },
      { title: 'Table & guest tracking', body: 'Record table start times and party sizes, and see active tables at a glance.' },
      { title: 'Stripe payments', body: 'Subscribe and update your card on Stripe’s secure checkout pages. Connecting your own Stripe account is also supported.' },
    ],
    proof: {
      heading: 'Proven in a real restaurant, every day',
      sub: "In-store ordering has been in daily production since January 2025 at Koryori Shiun (Tokyo), the founder's own restaurant — built and refined on the floor. Reservation management is a newer capability added after the first release, and is being refined the same way.",
      stats: [
        { value: '1.5 yrs', label: 'Running in a real restaurant' },
        { value: 'Solo-op', label: 'Runs a 2-floor micro restaurant' },
      ],
      body: 'No gourmet-site integrations or bloat — just the ordering essentials small restaurants need, polished until a solo-operated micro restaurant could run on it.',
    },
    subscription: {
      heading: 'A subscription that stays current',
      body: 'No large upfront cost. Adopt FEAST ORDER as a monthly subscription, with continuous feature updates.',
      points: [
        'In-store ordering and reservations both included in the base plan',
        'Start small with low upfront cost',
        'Subscribe online with a credit card (powered by Stripe)',
        'Continuous feature updates',
      ],
      cta: 'Start free (sign up)',
    },
    ctaBand: {
      title: 'Try it free, starting today',
      body: 'No sales calls, no paperwork. Sign up with your Google or Apple account in minutes — 30-day free trial, then \u00a53,300/month (tax included).',
      button: 'Start free (sign up)',
    },
    footer: { corp: 'Company (corporate site)', service: 'Service', rights: 'FEAST K.K.' },
    langLabel: '日本語',
  },
};

// ===== SEO: <head> 制御 =====
const upsertMeta = (selector: string, attr: 'name' | 'property', key: string, content: string): void => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel: string, href: string, hreflang?: string): void => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(sel);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const useSeo = (lang: Lang): void => {
  useEffect(() => {
    const c = COPY[lang];
    document.documentElement.lang = c.htmlLang;
    document.title = c.metaTitle;

    upsertMeta('meta[name="description"]', 'name', 'description', c.metaDescription);
    upsertMeta('meta[name="robots"]', 'name', 'robots', 'index, follow');

    // Open Graph
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'FEAST ORDER');
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', c.metaTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', c.metaDescription);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', SITE_URL);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', OGP_IMAGE);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', lang === 'ja' ? 'ja_JP' : 'en_US');

    // Twitter
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', c.metaTitle);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', c.metaDescription);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', OGP_IMAGE);

    // canonical + hreflang
    upsertLink('canonical', SITE_URL);
    upsertLink('alternate', SITE_URL, 'ja');
    upsertLink('alternate', SITE_URL, 'en');
    upsertLink('alternate', SITE_URL, 'x-default');

    // JSON-LD 構造化データ
    const ldId = 'feast-jsonld';
    document.getElementById(ldId)?.remove();
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = ldId;
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'FEAST株式会社',
          alternateName: 'FEAST K.K.',
          url: CORP_URL,
          logo: `${SITE_URL}lp/icon.png`,
        },
        {
          '@type': 'SoftwareApplication',
          name: 'FEAST ORDER',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          description: c.metaDescription,
          image: OGP_IMAGE,
          url: SITE_URL,
          offers: {
            '@type': 'Offer',
            category: 'Subscription',
            // Web 版はサブスク契約可能（Stripe Checkout）のため InStock（旧: PreOrder）
            availability: 'https://schema.org/InStock',
          },
          publisher: { '@type': 'Organization', name: 'FEAST株式会社', url: CORP_URL },
        },
      ],
    });
    document.head.appendChild(ld);
  }, [lang]);
};

// ===== アイコン（インラインSVG） =====
const Dot: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12l5 5 9-10" stroke="#F46B4D" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.4 12.7c0-2 1.6-3 1.7-3-0.9-1.4-2.4-1.6-2.9-1.6-1.2-0.1-2.4 0.7-3 0.7s-1.6-0.7-2.6-0.7c-1.3 0-2.6 0.8-3.3 2-1.4 2.5-0.4 6.1 1 8.1 0.7 1 1.4 2 2.5 2 1 0 1.3-0.6 2.5-0.6s1.5 0.6 2.6 0.6c1.1 0 1.8-1 2.4-2 0.8-1.1 1.1-2.2 1.1-2.3-0.1 0-2.1-0.8-2.1-3zM14.6 6.3c0.6-0.7 1-1.6 0.9-2.6-0.8 0-1.9 0.6-2.5 1.3-0.5 0.6-1 1.6-0.9 2.5 0.9 0.1 1.8-0.5 2.5-1.2z" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.6 2.3c-0.3 0.3-0.5 0.7-0.5 1.3v16.8c0 0.6 0.2 1 0.5 1.3l0.1 0.1L13 12.1v-0.2L3.7 2.2l-0.1 0.1z" />
    <path d="M16 15.3l-3-3v-0.2l3-3 0.1 0.1 3.6 2c1 0.6 1 1.5 0 2.1l-3.6 2-0.1 0.1z" opacity="0.85" />
    <path d="M16.1 15.2L13 12.1 3.6 21.7c0.4 0.4 1 0.4 1.7 0.1l10.8-6.6z" opacity="0.9" />
    <path d="M16.1 9L5.3 2.3C4.6 2 4 2 3.6 2.4L13 12l3.1-3z" opacity="0.7" />
  </svg>
);

// ===== ストアバッジ =====
const StoreBadge: React.FC<{
  icon: React.ReactNode;
  line1: string;
  line2: string;
  href: string;
  comingSoon: string;
}> = ({ icon, line1, line2, href, comingSoon }) => {
  const available = href.length > 0;
  const inner = (
    <span className="badge-inner">
      <span className="badge-icon">{icon}</span>
      <span className="badge-text">
        <span className="badge-l1">{line1}</span>
        <span className="badge-l2">{line2}</span>
      </span>
      {!available && <span className="badge-soon">{comingSoon}</span>}
    </span>
  );
  if (available) {
    return (
      <a className="store-badge" href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return (
    <span className="store-badge store-badge--disabled" aria-disabled="true">
      {inner}
    </span>
  );
};

// ===== ロゴマーク =====
const LogoMark: React.FC = () => (
  <span className="logo">
    <img className="logo-img" src={ASSET('/lp/feast-logo.svg')} alt="FEAST" width={96} height={28} />
    <span className="logo-order">ORDER</span>
  </span>
);

// ===== 本体 =====
const LandingPage: React.FC = () => {
  const [lang, setLang] = useState<Lang>('ja');
  useSeo(lang);
  const c = COPY[lang];

  // 新規登録受付ゲート（ADR 0059 / 0060）。本番（SIGNUP_ENABLED=false）はローンチ前のため
  // 登録動線そのものを表示しない（null を返す）。stg/local は従来どおり /register へ遷移する。
  const registerCta = (className: string, label: string) =>
    SIGNUP_ENABLED ? (
      <a className={className} href="/register">
        {label}
      </a>
    ) : null;

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === 'ja' ? 'en' : 'ja'));
  }, []);

  return (
    <div className="lp-root">
      <style>{CSS}</style>

      {/* Header */}
      <header className="lp-header">
        <div className="container header-inner">
          <LogoMark />
          {/* ナビリンクはモバイルで崩れる & フッターに同一導線があるため、言語切替のみ置く。 */}
          <nav className="header-nav">
            <button className="lang-toggle" type="button" onClick={toggleLang} aria-label="Switch language">
              {c.langLabel}
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <span className="hero-badge">{c.hero.badge}</span>
            <h1 className="hero-title">{c.hero.title}</h1>
            <p className="hero-lead">{c.hero.lead}</p>
            <div className="hero-cta">
              {registerCta('btn btn-primary', c.hero.primaryCta)}
              <a className="btn btn-ghost" href={CORP_SERVICE_URL} target="_blank" rel="noopener noreferrer">
                {c.hero.secondaryCta}
              </a>
            </div>
            <p className="hero-note">{c.hero.note}</p>
            <div className="platforms">
              <span className="platforms-web">
                <Dot /> {c.platforms.web}：<strong>{c.platforms.webState}</strong>
              </span>
              <div className="badges">
                <StoreBadge
                  icon={<AppleIcon />}
                  line1="Download on the"
                  line2="App Store"
                  href={APP_STORE_URL}
                  comingSoon={c.platforms.comingSoon}
                />
                <StoreBadge
                  icon={<PlayIcon />}
                  line1="GET IT ON"
                  line2="Google Play"
                  href={GOOGLE_PLAY_URL}
                  comingSoon={c.platforms.comingSoon}
                />
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <img
              className="hero-phone"
              src={ASSET('/lp/app-order-notification.png')}
              alt={lang === 'ja' ? 'FEAST ORDER 注文受付画面（新着注文のプッシュ通知）' : 'FEAST ORDER order inbox with push notification'}
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Screens */}
      <section className="section screens">
        <div className="container">
          <h2 className="section-title">{c.screensHeading}</h2>
          <p className="section-sub">{c.screensSub}</p>
          <div className="screens-grid">
            {c.screens.map((s) => (
              <figure key={s.caption} className={`screen-card screen-card--${s.frame}`}>
                <div className={`device device--${s.frame}`}>
                  <img src={s.src} alt={s.alt} loading="lazy" />
                </div>
                <figcaption>
                  <h3>{s.caption}</h3>
                  <p>{s.desc}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section features">
        <div className="container">
          <h2 className="section-title">{c.featuresHeading}</h2>
          <div className="features-grid">
            {c.features.map((f) => (
              <div key={f.title} className="feature-card">
                <span className="feature-bullet" aria-hidden="true">
                  <Dot />
                </span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof（実運用実績） */}
      <section className="section proof">
        <div className="container">
          <h2 className="section-title">{c.proof.heading}</h2>
          <p className="section-sub">{c.proof.sub}</p>
          <div className="proof-grid">
            {c.proof.stats.map((st) => (
              <div key={st.label} className="proof-card">
                <div className="proof-value">{st.value}</div>
                <div className="proof-label">{st.label}</div>
              </div>
            ))}
          </div>
          <p className="proof-body">{c.proof.body}</p>
        </div>
      </section>

      {/* Subscription */}
      <section className="section subscription">
        <div className="container subscription-inner">
          <div>
            <h2 className="section-title section-title--left">{c.subscription.heading}</h2>
            <p className="subscription-body">{c.subscription.body}</p>
            <ul className="subscription-points">
              {c.subscription.points.map((p) => (
                <li key={p}>
                  <Dot />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            {registerCta('btn btn-primary', c.subscription.cta)}
          </div>
          <div className="subscription-visual">
            <img
              src={ASSET('/lp/app-home.png')}
              alt={lang === 'ja' ? 'FEAST ORDER ホーム（複数店舗の切替）' : 'FEAST ORDER home with multi-store switching'}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA band（新規登録の訴求バンド）。登録動線を出さない本番（ADR 0060）ではセクションごと非表示。 */}
      {SIGNUP_ENABLED && (
        <section className="cta-band">
          <div className="container cta-band-inner">
            <div>
              <h2>{c.ctaBand.title}</h2>
              <p>{c.ctaBand.body}</p>
            </div>
            {registerCta('btn btn-light', c.ctaBand.button)}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="lp-footer">
        <div className="container footer-inner">
          <LogoMark />
          <nav className="footer-nav">
            <a href={CORP_URL} target="_blank" rel="noopener noreferrer">
              {c.footer.corp}
            </a>
            <a href={CORP_SERVICE_URL} target="_blank" rel="noopener noreferrer">
              {c.footer.service}
            </a>
          </nav>
          <small>© {new Date().getFullYear()} {c.footer.rights}</small>
        </div>
      </footer>
    </div>
  );
};

// ===== スタイル =====
const CSS = `
.lp-root{font-family:Outfit,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue','Hiragino Kaku Gothic ProN','Noto Sans JP',Meiryo,sans-serif;color:#222;background:#fff;line-height:1.7;-webkit-font-smoothing:antialiased;}
.lp-root *{box-sizing:border-box;}
.container{width:100%;max-width:1120px;margin:0 auto;padding:0 24px;}
.lp-root a{text-decoration:none;}
.lp-root a:not(.btn){color:inherit;}

/* logo */
.logo{display:inline-flex;align-items:flex-end;gap:8px;font-weight:800;}
.logo-img{height:28px;width:auto;display:block;}
.logo-order{color:#F46B4D;font-size:19px;letter-spacing:1px;font-weight:800;line-height:1;transform:translateY(0.12em);}

/* header */
.lp-header{position:sticky;top:0;z-index:20;background:rgba(255,255,255,.9);backdrop-filter:saturate(180%) blur(10px);border-bottom:1px solid #f0f0f0;}
.header-inner{display:flex;align-items:center;justify-content:space-between;height:64px;}
.header-nav{display:flex;align-items:center;gap:22px;font-size:14px;font-weight:600;}
.header-nav a:hover{color:#F46B4D;}
.lang-toggle{border:1px solid #e2c8c1;background:#fff;color:#D94E30;font-weight:700;font-size:13px;padding:7px 14px;border-radius:999px;cursor:pointer;transition:.15s;}
.lang-toggle:hover{background:#FFF1EE;}

/* hero */
.hero{background:radial-gradient(1200px 500px at 80% -10%,#FFE7DF 0%,#FFF6F3 45%,#ffffff 80%);padding:64px 0 40px;overflow:hidden;}
.hero-inner{display:grid;grid-template-columns:1.05fr .95fr;gap:40px;align-items:center;}
.hero-badge{display:inline-block;background:#fff;border:1px solid #F6CEC2;color:#D94E30;font-weight:700;font-size:12.5px;padding:7px 14px;border-radius:999px;letter-spacing:.3px;}
.hero-title{font-size:44px;line-height:1.25;font-weight:800;margin:18px 0 0;letter-spacing:.2px;}
.hero-lead{font-size:16.5px;color:#444;margin:18px 0 0;max-width:560px;}
.hero-cta{display:flex;flex-wrap:wrap;gap:12px;margin-top:26px;}
.hero-note{font-size:13px;color:#888;margin-top:14px;}

.btn{display:inline-flex;align-items:center;justify-content:center;font-weight:700;font-size:15px;padding:13px 24px;border-radius:999px;cursor:pointer;transition:.18s;border:1.5px solid transparent;}
.btn-primary{background:#F46B4D;color:#fff;box-shadow:0 8px 22px rgba(244,107,77,.32);}
.btn-primary:hover{background:#E15D40;transform:translateY(-1px);}
.btn-ghost{background:#fff;color:#D94E30;border-color:#F0C2B6;}
.btn-ghost:hover{background:#FFF1EE;}
.btn-light{background:#fff;color:#D94E30;}
.btn-light:hover{transform:translateY(-1px);}

/* platforms */
.platforms{margin-top:30px;}
.platforms-web{display:inline-flex;align-items:center;gap:7px;font-size:14px;color:#555;}
.platforms-web strong{color:#B45309;}
.badges{display:flex;flex-wrap:wrap;gap:12px;margin-top:14px;}
.store-badge{display:inline-flex;background:#222;color:#fff;border-radius:12px;padding:9px 16px;min-width:172px;transition:.18s;}
.store-badge:hover{transform:translateY(-1px);}
.store-badge--disabled{opacity:.55;cursor:default;}
.badge-inner{display:flex;align-items:center;gap:10px;position:relative;width:100%;}
.badge-icon{display:flex;}
.badge-text{display:flex;flex-direction:column;line-height:1.15;}
.badge-l1{font-size:10px;opacity:.85;}
.badge-l2{font-size:16px;font-weight:700;}
.badge-soon{position:absolute;right:-6px;top:-16px;background:#F46B4D;color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;white-space:nowrap;}

/* hero visual */
.hero-visual{display:flex;justify-content:center;}
.hero-phone{width:284px;max-width:100%;height:auto;padding:9px;background:#1B1B1F;border-radius:38px;box-shadow:0 26px 54px rgba(180,80,55,.30);}

/* sections */
.section{padding:72px 0;}
.section-title{text-align:center;font-size:30px;font-weight:800;margin:0;}
.section-title--left{text-align:left;}
.section-sub{text-align:center;color:#666;margin:12px 0 0;font-size:16px;}

/* screens */
.screens{background:#FBFAF9;}
.screens-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;margin-top:44px;align-items:start;}
.screen-card{margin:0;text-align:center;}
.device{max-width:236px;margin:0 auto;padding:8px;background:#1B1B1F;border-radius:32px;box-shadow:0 18px 40px rgba(0,0,0,.20);}
.device img{width:100%;height:auto;display:block;border-radius:24px;}
.screen-card figcaption h3{font-size:17px;font-weight:700;margin:22px 0 6px;}
.screen-card figcaption p{font-size:14px;color:#666;margin:0 auto;max-width:300px;}

/* features */
.features-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:44px;}
.proof{background:#FFF7F4;}
.proof-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;margin-top:36px;max-width:560px;margin-left:auto;margin-right:auto;}
.proof-card{background:#fff;border:1px solid #F6CEC2;border-radius:16px;padding:22px 16px;text-align:center;}
.proof-value{font-size:28px;font-weight:700;color:#F46B4D;letter-spacing:-.01em;}
.proof-label{font-size:13px;color:#666;margin-top:6px;}
.proof-body{max-width:720px;margin:28px auto 0;text-align:center;color:#555;font-size:15px;line-height:1.9;}
.feature-card{background:#fff;border:1px solid #f0eeec;border-radius:16px;padding:26px 24px;transition:.18s;}
.feature-card:hover{border-color:#F6CEC2;box-shadow:0 12px 30px rgba(180,80,55,.10);transform:translateY(-2px);}
.feature-bullet{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:12px;background:#FFF1EE;margin-bottom:14px;}
.feature-card h3{font-size:17px;font-weight:700;margin:0 0 8px;}
.feature-card p{font-size:14.5px;color:#5a5a5a;margin:0;}

/* subscription */
.subscription{background:linear-gradient(180deg,#fff,#FFF6F3);}
.subscription-inner{display:grid;grid-template-columns:1.1fr .9fr;gap:48px;align-items:center;}
.subscription-body{font-size:16px;color:#444;margin:18px 0 22px;}
.subscription-points{list-style:none;padding:0;margin:0 0 28px;}
.subscription-points li{display:flex;align-items:center;gap:10px;font-size:15.5px;font-weight:600;margin-bottom:12px;}
.subscription-visual{display:flex;justify-content:center;}
.subscription-visual img{width:248px;max-width:100%;height:auto;padding:8px;background:#1B1B1F;border-radius:34px;box-shadow:0 22px 46px rgba(180,80,55,.24);}

/* cta band */
.cta-band{background:#222;color:#fff;padding:56px 0;}
.cta-band-inner{display:flex;align-items:center;justify-content:space-between;gap:28px;flex-wrap:wrap;}
.cta-band h2{font-size:24px;font-weight:800;margin:0 0 8px;}
.cta-band p{margin:0;color:#cfcfcf;font-size:15px;}

/* footer */
.lp-footer{background:#fff;border-top:1px solid #f0f0f0;padding:36px 0;}
.footer-inner{display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap;}
.footer-nav{display:flex;gap:22px;font-size:14px;font-weight:600;}
.footer-nav a:hover{color:#F46B4D;}
.lp-footer small{color:#999;font-size:13px;}

/* responsive */
@media (max-width:880px){
  .hero-inner{grid-template-columns:1fr;text-align:center;}
  .hero-lead{margin-left:auto;margin-right:auto;}
  .hero-cta,.platforms-web,.badges{justify-content:center;}
  .platforms{display:flex;flex-direction:column;align-items:center;}
  .hero-title{font-size:34px;}
  .hero-visual{margin-top:30px;}
  .screens-grid{grid-template-columns:1fr;max-width:420px;margin-left:auto;margin-right:auto;}
  .features-grid{grid-template-columns:1fr 1fr;}
  .subscription-inner{grid-template-columns:1fr;}
  .section-title--left{text-align:center;}
  .subscription-points{display:inline-block;text-align:left;}
  .subscription .btn{display:inline-flex;}
  .subscription-visual{margin-top:24px;}
}
@media (max-width:540px){
  .features-grid{grid-template-columns:1fr;}
  .header-nav{gap:14px;}
  .proof-grid{grid-template-columns:repeat(2,1fr);}
  .hero-title{font-size:28px;}
  .cta-band-inner{flex-direction:column;text-align:center;}
}
`;

export default LandingPage;
